import { Sentry } from "@/hooks/sentry"

export type ExternalScreenType = `Ext:${string}`

export type AppScreen =
  | "Account"
  | "Account Debug"
  | "Account Edit Personal Info"
  | "Account Edit Name"
  | "Account Edit Email"
  | "Account Edit Phone Number"
  | "Account Edit Notifications"
  | "Feedback"
  | "Force Upgrade"
  | "Events"
  | "Event Details"
  | "Market Trends Region Search"
  | "Analysis Loading"
  | "Analyze"
  | "Analyze Property Search"
  | "Analyze Property Import"
  | "Borrower"
  | "Cash Available"
  | "Closing Date"
  | "Confirm Borrower Info"
  | "Confirm Registration"
  | "Contract Status"
  | "Credit Score"
  | "Timeline"
  | "Enter Loan Summary"
  | "Home"
  | "Import"
  | "Loan Dashboard"
  | "Loan Dashboard Details"
  | "Loan Dashboard Index"
  | "Loan List"
  | "Loan App Submitted Successfully"
  | "Loan App Contact Lender"
  | "Loan Confirm"
  | "Loan Requests"
  | "Loan Property Loading"
  | "Loan Property Search"
  | "Loan Question"
  | "Loan Setup - Qualification"
  | "Loan Setup - Property Info"
  | "Loan Setup - Select Product"
  | "Loan Setup - Confirm and Submit"
  | "Loan Setup - Success"
  | "Loans"
  | "Loans - Landing"
  | "Loan Products"
  | "Analyzer Popover"
  | "PreApproval Letter Modal"
  | "PreQual LandingPage"
  | "PreQual Question"
  | "PreQual Borrower Question"
  | "PreQual Properties Flipped Question"
  | "PreQual Rentals Owned Question"
  | "PreQual Cash Available Question"
  | "PreQual Credit Score Question"
  | "PreQual Timeline Question"
  | "PreQual Intent Status Question"
  | "Properties"
  | "Properties Flipped"
  | "Property Not Found"
  | "Purchase Date"
  | "Register"
  | "Rehab SOW"
  | "Rehab Category"
  | "Loan Rehab Budget"
  | "Rentals Owned"
  | "Sign In"
  | "Sign In - Google"
  | "Reset Password Request"
  | "Reset Password"
  | "Subject Property"
  | "Subject Property Comps"
  | "Subject Property Calculate"
  | "Subject Property Street"
  | "Edit Subject Property"
  | "Full Screen Comp List"
  | "Comp Detail"
  | "Comp Map"
  | "Comp Map Placeholder"
  | "Customize Comps"
  | "Customize Comps Placeholder"
  | "Search Comps"
  | "Rewards"
  | "Onboarding Confirm"
  | "Unknown"
  | "Ask User About Flips"
  | "Soft Credit Poll - Internal"
  | "Soft Credit Poll - External"
  | "Soft Credit Poll - Confirmation"
  | ExternalScreenType

const SCREEN_URL_MAP: Record<string, AppScreen> = {
  login: "Sign In",
  register: "Register",
  rewards: "Rewards",
  "(tabs)-rewards": "Rewards",
  index: "Home",
  "(onboarding)-confirm": "Onboarding Confirm",
  "(onboarding)-register": "Register",
  "(onboarding)-login": "Sign In",
  "(onboarding)-google": "Sign In - Google",
  "(onboarding)-reset-password": "Reset Password Request",
  "(onboarding)-reset": "Reset Password",
  "(tabs)": "Home",
  "account-ask_about_flips": "Ask User About Flips",
  "(tabs)-analyze": "Analyze",
  "(tabs)-analyze-search": "Analyze Property Search",
  "(tabs)-analyze-import": "Analyze Property Import",
  "(tabs)-analyze-search-[placeId]": "Analysis Loading",
  "(tabs)-analyze-[analysisId]": "Subject Property",
  "(tabs)-analyze-[analysisId]-map": "Comp Map",
  "(tabs)-analyze-[analysisId]-customizecomps": "Customize Comps",
  "(tabs)-analyze-[analysisId]-street": "Subject Property Street",
  "(tabs)-analyze-[analysisId]-comp-[compId]": "Comp Detail",
  "(tabs)-analyze-marketdata-qualitativefeedback": "Feedback",
  "(tabs)-analyze-marketdata-regionsearch": "Market Trends Region Search",
  "(tabs)-properties-[analysisId]": "Subject Property",
  "(tabs)-properties-[analysisId]-map": "Comp Map",
  "(tabs)-properties-[analysisId]-customizecomps": "Customize Comps",
  "(tabs)-properties-[analysisId]-searchcomps-[placeId]": "Search Comps",
  "(tabs)-properties-[analysisId]-street": "Subject Property Street",
  "(tabs)-properties-[analysisId]-comp-[compId]": "Comp Detail",
  "(tabs)-properties": "Properties",
  "(tabs)-loans": "Loans",
  "(tabs)-loans-landing": "Loans - Landing",
  "(tabs)-loans-products": "Loan Products",
  "(tabs)-loans-setup": "Loan Setup - Qualification",
  "(tabs)-loans-setup-property": "Loan Setup - Property Info",
  "(tabs)-loans-setup-qualifications": "Loan Setup - Qualification",
  "(tabs)-loans-setup-product": "Loan Setup - Select Product",
  "(tabs)-loans-setup-submit": "Loan Setup - Confirm and Submit",
  "(tabs)-loans-setup-success": "Loan Setup - Success",
  "(tabs)-loans-search": "Loan Property Search",
  "(tabs)-loans-search-[placeId]": "Loan Property Loading",
  "(tabs)-loans-[loanId]": "Loan Confirm",
  "(tabs)-loans-[loanId]-draws": "Loan Rehab Budget",
  "(tabs)-loans-[loanId]-calculator": "Enter Loan Summary",
  "(tabs)-loans-[loanId]-softcreditpull": "Soft Credit Poll - Internal",
  "(tabs)-loans-[loanId]-contact": "Confirm Borrower Info",
  "(tabs)-loans-[loanId]-success": "Loan App Submitted Successfully",
  "(tabs)-loans-[loanId]-setup-borrower-info": "Loan Question",
  "(tabs)-loans-[loanId]-setup-borrower-info-[questionKey]": "Loan Question",
  "(tabs)-loans-[loanId]-setup-loan-info": "Loan Question",
  "(tabs)-loans-[loanId]-setup-loan-info-[questionKey]": "Loan Question",
  "(tabs)-loans-[loanId]-[questionKey]": "Loan Question",
  "(tabs)-loans-[loanId]-contactLenderSuccess": "Loan App Contact Lender",
  "(tabs)-loans-[loanId]-rehab": "Rehab SOW",
  "(tabs)-loans-[loanId]-requests": "Loan Requests",
  "(tabs)-loans-dashboard": "Loan Dashboard Index",
  "(tabs)-loans-[loanId]-dashboard": "Loan Dashboard",
  "(tabs)-loans-[loanId]-dashboard-details": "Loan Dashboard Details",
  "(tabs)-events": "Events",
  account: "Account",
  "account-debug": "Account Debug",
  "account-personal-info": "Account Edit Personal Info",
  "account-personal-info-email": "Account Edit Email",
  "account-personal-info-phone": "Account Edit Phone Number",
  "account-personal-info-name": "Account Edit Name",
  "account-notifications": "Account Edit Notifications",
  "pre-qual-[questionKey]": "PreQual Question",
  "pre-qual": "PreQual Question",
  "pre-qual-questions": "PreQual Question",
  "pre-qual-landing": "PreQual LandingPage",
  "[...missing]": "Unknown",
  softcreditpull: "Soft Credit Poll - External",
  "softcreditpull-confirmation": "Soft Credit Poll - Confirmation",
}

export const getScreenName = (screenSegments: string) => {
  if (screenSegments in SCREEN_URL_MAP) {
    return SCREEN_URL_MAP[screenSegments]
  }

  // Annoy engineers if they don't update the screen mapping for new/changed screens
  Sentry.captureException(new Error(`No ScreenName for "${screenSegments}"`))
  console.error(`No ScreenName for "${screenSegments}"`)

  return "Unknown"
}
